import { useEffect } from 'react';
import { Button, IconButton, Typography, Stack, useMediaQuery } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { MOBILE_BREAKPOINT, btn } from '../styles/ds';

/**
 * Pagination footer for dynamically-sized pages (see hooks/useFitCount).
 * Deliberately NOT react-admin's own <Pagination> — that one assumes a
 * fixed perPage and re-fetches from the server on every page change. This
 * one just windows over already-fetched `total` items, `perPage` at a
 * time, where `perPage` itself changes live as the container resizes —
 * "page 2 of 3" can become "page 2 of 2" mid-session with no server
 * round-trip, which is the whole point.
 */
const FitPagination = ({ page, setPage, total, perPage }) => {
  const isMobile = useMediaQuery(`(max-width:${MOBILE_BREAKPOINT}px)`);
  const pageCount = Math.max(1, Math.ceil(total / perPage));

  // If the container grew (or shrank) enough that the current page number
  // no longer exists, clamp back onto the last valid page instead of
  // showing an empty page.
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount, setPage]);

  if (total === 0) return null;

  if (isMobile) {
    const visibleCount = Math.min(page * perPage, total);
    const remaining = total - visibleCount;
    return (
      <Stack className="crm-pager" alignItems="center" spacing={1.5} sx={{ py: 2 }}>
        <Typography role="status" aria-live="polite" fontSize={13} color="text.secondary">
          מוצגות {visibleCount} מתוך {total} רשומות
        </Typography>
        {remaining > 0 && (
          <Button variant="outlined" sx={btn.gold}
            onClick={() => setPage(p => Math.min(pageCount, p + 1))}>
            הצג עוד {Math.min(perPage, remaining)}
          </Button>
        )}
      </Stack>
    );
  }

  return (
    <Stack className="crm-pager" direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ py: 1.5 }}>
      <IconButton size="small" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} aria-label="עמוד קודם">
        <ChevronRightIcon fontSize="small" />
      </IconButton>
      <Typography fontSize={13} color="text.secondary">
        עמוד {page} מתוך {pageCount} · {total} סה״כ
      </Typography>
      <IconButton size="small" disabled={page >= pageCount} onClick={() => setPage(p => Math.min(pageCount, p + 1))} aria-label="עמוד הבא">
        <ChevronLeftIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
};

export default FitPagination;
